import { useMemo } from "react";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { DraftsTab } from "@/components/features/EmailAi/DraftsTab";
import { MailboxTab } from "@/components/features/EmailAi/MailboxTab";
import { SettingsTab } from "@/components/features/EmailAi/SettingsTab";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Stack, Tabs } from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import type { TabItemType } from "@/types/common.types";

export const Route = createFileRoute("/_authenticated/email-ai/")({
  component: EmailAiPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function EmailAiPage() {
  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "inbox" });

  useBreadcrumb([
    {
      title: "Email AI",
      href: "/email-ai",
      icon: <IconMail size={16} stroke={1.5} />,
    },
  ]);

  const tabsData: TabItemType[] = useMemo(
    () => [
      { value: "inbox", label: "Inbox", component: <MailboxTab folder="inbox" /> },
      { value: "sent", label: "Sent", component: <MailboxTab folder="sentitems" /> },
      { value: "drafts", label: "Drafts", component: <DraftsTab /> },
      { value: "deleted", label: "Deleted", component: <MailboxTab folder="deleteditems" /> },
      { value: "junk", label: "Junk", component: <MailboxTab folder="junkemail" /> },
      { value: "settings", label: "Settings", component: <SettingsTab /> },
    ],
    [],
  );

  return (
    <Stack gap="md">
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          {tabsData.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabsData.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} pt="md">
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
}
